import supabase from '@/api/supabase';
import { Badge, Button } from '@mui/material';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import { blueGrey, grey } from '@mui/material/colors';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useQuery } from '@tanstack/react-query';
import { HelpCircle, LogOut, User2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import useCurrentUser from '../user/useCurrentUser';
import LogOutButton from './LogOutButton';
import './NavUserButton.scss';

export default function UserMenu() {
  const [anchorEl, setAnchorEl] = useState(null);
  const { currentUser, loading } = useCurrentUser();
  const open = Boolean(anchorEl);
  const navigate = useNavigate();

  // Query for total unread messages count from all senders
  const { data: unreadCount = 0, refetch } = useQuery({
    queryKey: ['unreadMessages', currentUser?.empUserId],
    queryFn: async () => {
      if (!currentUser?.empUserId) return 0;

      // Get total unread count from all senders
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('receiver_id', currentUser.empUserId)
        .eq('read', false);

      if (error) {
        console.error('Error fetching unread messages:', error);
        return 0;
      }

      return count ?? 0;
    },
    enabled: !!currentUser?.empUserId,
    // Reduced polling interval as backup
    refetchInterval: 5000, // Backup polling every 5 seconds
  });

  // Set up real-time subscription for new messages
  useEffect(() => {
    if (!currentUser?.empUserId) return;

    // Subscribe to changes in the messages table
    const subscription = supabase
      .channel('unread-messages')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${currentUser.empUserId}`,
        },
        () => {
          // Refetch the count when any message changes
          refetch();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [currentUser?.empUserId, refetch]);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfileRedirect = () => {
    navigate(`/profile/${currentUser.empUserId}`);
    handleClose();
  };

  const handleHelpCenter = () => {
    toast.success('Coming soon');
    handleClose();
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return null;
  }

  const rolesArray = Array.isArray(currentUser.empRole)
    ? currentUser.empRole
    : currentUser.empRole?.split('/') || [];

  return (
    <div className="user-menu">
      <Button className="avatar-button" onClick={handleClick}>
        <Badge
          badgeContent={unreadCount}
          color="error"
          overlap="circular"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Avatar className="avatar" sizes="small" src={currentUser.empAvatar} alt="User Avatar" />
        </Badge>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'avatar-button',
        }}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: '#f9f9f9',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <MenuItem>
          <div
            className="profile-info"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Typography variant="subtitle1" sx={{ marginRight: '8px' }}>
              {currentUser.empName}
            </Typography>

            <div className="nav_user_button_roles" style={{ display: 'flex', gap: '4px' }}>
              {rolesArray.map((role, index) => (
                <Chip
                  key={index}
                  label={role}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '8px',
                    color: blueGrey[800],
                    backgroundColor: '#eff6ff',
                    border: '1px solid rgba(222, 207, 148, 0.5)',
                    height: '14px',
                  }}
                />
              ))}
            </div>
          </div>
        </MenuItem>

        <MenuItem onClick={handleProfileRedirect}>
          <ListItemIcon>
            <User2 size={16} />
          </ListItemIcon>
          <Typography
            sx={{
              fontSize: 12,
              color: grey[600],
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            }}
            onClick={handleProfileRedirect}
          >
            View Profile
          </Typography>
        </MenuItem>

        <MenuItem onClick={handleHelpCenter}>
          <ListItemIcon>
            <HelpCircle size={16} />
          </ListItemIcon>
          <Typography sx={{ fontSize: 12, color: grey[600] }}>Help Center</Typography>
        </MenuItem>

        <MenuItem>
          <ListItemIcon>
            <LogOut size={16} />
          </ListItemIcon>
          <LogOutButton />
        </MenuItem>
      </Menu>
    </div>
  );
}
